// Supabase Database Service for IdeaVerdict
import { supabase } from './config';
import type {
    Json,
    UserDocument,
    IdeaRecord,
    CreditHistoryRecord,
    CouponDocument,
    DowngradeRequest,
    DowngradeRule,
    RefundRecord,
} from './types';

// ─── Helpers: Row ↔ App type converters ─────────────────────────────────────

function rowToUserDoc(row: Record<string, unknown>): UserDocument {
    const prefs = row.notification_prefs as UserDocument['notificationPrefs'] | null | undefined;
    return {
        name: row.name as string,
        email: row.email as string,
        plan: row.plan as string,
        credits: row.credits as number,
        maxCredits: row.max_credits as number,
        createdAt: row.created_at as string,
        isEmailVerified: row.is_email_verified as boolean,
        couponUsed: (row.coupon_used as string) ?? null,
        stripeCustomerId: (row.stripe_customer_id as string) ?? undefined,
        selectedCurrency: (row.selected_currency as string) ?? undefined,
        language: (row.language as string) ?? undefined,
        lastCreditReset: (row.last_credit_reset as string) ?? undefined,
        avatarUrl: (row.avatar_url as string) ?? null,
        phone: (row.phone as string) ?? null,
        timezone: (row.timezone as string) ?? undefined,
        notificationPrefs: prefs ?? undefined,
        is_admin: (row.is_admin as boolean) ?? false,
        is_blocked: (row.is_blocked as boolean) ?? false,
        subscriptionStatus: ((row.subscription_status as string) ?? 'active') as 'active' | 'cancelled' | 'overdue',
        cancelledAt: (row.cancelled_at as string) ?? null,
        cancelledEffectiveDate: (row.cancelled_effective_date as string) ?? null,
        cancellationReason: (row.cancellation_reason as string) ?? null,
    };
}

function getCurrentMonth(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// ─── User Document ────────────────────────────────────────────────────────────

/** Create a new user row in public.users */
export async function createUserDocument(
    uid: string,
    data: Omit<UserDocument, 'lastCreditReset'>
): Promise<void> {
    const { error } = await supabase.from('users').upsert({
        id: uid,
        name: data.name,
        email: data.email,
        plan: data.plan,
        credits: data.credits,
        max_credits: data.maxCredits,
        is_email_verified: data.isEmailVerified,
        coupon_used: data.couponUsed,
        last_credit_reset: getCurrentMonth(),
    }, { onConflict: 'id' });

    if (error) throw error;
}

/** Get user document from public.users */
export async function getUserDocument(uid: string): Promise<UserDocument | null> {
    const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', uid)
        .single();

    if (error) {
        if (error.code === 'PGRST116') return null; // not found
        throw error;
    }
    return data ? rowToUserDoc(data as Record<string, unknown>) : null;
}

/** Update user document fields */
export async function updateUserDocument(
    uid: string,
    data: Partial<UserDocument>
): Promise<void> {
    const updatePayload: Record<string, unknown> = {};
    if (data.name !== undefined) updatePayload.name = data.name;
    if (data.email !== undefined) updatePayload.email = data.email;
    if (data.plan !== undefined) updatePayload.plan = data.plan;
    if (data.credits !== undefined) updatePayload.credits = data.credits;
    if (data.maxCredits !== undefined) updatePayload.max_credits = data.maxCredits;
    if (data.isEmailVerified !== undefined) updatePayload.is_email_verified = data.isEmailVerified;
    if (data.couponUsed !== undefined) updatePayload.coupon_used = data.couponUsed;
    if (data.stripeCustomerId !== undefined) updatePayload.stripe_customer_id = data.stripeCustomerId;
    if (data.selectedCurrency !== undefined) updatePayload.selected_currency = data.selectedCurrency;
    if (data.language !== undefined) updatePayload.language = data.language;
    if (data.lastCreditReset !== undefined) updatePayload.last_credit_reset = data.lastCreditReset;
    if (data.avatarUrl !== undefined) updatePayload.avatar_url = data.avatarUrl;
    if (data.phone !== undefined) updatePayload.phone = data.phone;
    if (data.timezone !== undefined) updatePayload.timezone = data.timezone;
    if (data.notificationPrefs !== undefined) updatePayload.notification_prefs = data.notificationPrefs;

    const { error } = await supabase
        .from('users')
        .update(updatePayload)
        .eq('id', uid);

    if (error) throw error;
}

/**
 * Delete the currently-authenticated user's account. Cascades via
 * SECURITY DEFINER so all `public.*` rows are removed and payment methods
 * are invalidated first. Admins are rejected by the RPC.
 */
export async function userDeleteAccountSelf(): Promise<void> {
    const { error } = await supabase.rpc('user_delete_account_self' as any);
    if (error) throw error;
    // Invalidate any cached session locally after the server cascade.
    try { await supabase.auth.signOut(); } catch { /* noop */ }
}

/** Subscribe to real-time user document changes */
export function onUserDocumentChange(
    uid: string,
    callback: (data: UserDocument | null) => void
): () => void {
    const channel = supabase
        .channel(`users:${uid}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'users',
                filter: `id=eq.${uid}`,
            },
            (payload) => {
                if (payload.eventType === 'DELETE') {
                    callback(null);
                } else {
                    callback(rowToUserDoc(payload.new as Record<string, unknown>));
                }
            }
        )
        .subscribe();

    return () => {
        supabase.removeChannel(channel);
    };
}

// ─── Avatar Upload ────────────────────────────────────────────────────────────

/**
 * Upload a profile image to Supabase Storage and save the public URL
 * to the user's profile. Returns the public URL.
 */
export async function uploadAvatar(uid: string, file: File): Promise<string> {
    // Validate file type
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
        throw new Error('Invalid file type. Only JPG, PNG, GIF and WebP are allowed.');
    }
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
        throw new Error('File too large. Maximum size is 2MB.');
    }

    const ext = file.name.split('.').pop() ?? 'jpg';
    const filePath = `${uid}/avatar.${ext}`;

    // Upload (upsert overwrites existing avatar)
    const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true, contentType: file.type });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

    // Cache busting: append timestamp so the browser instantly loads the new image instead of caching the old one
    const publicUrl = `${urlData.publicUrl}?t=${new Date().getTime()}`;

    // Save to user profile
    await updateUserDocument(uid, { avatarUrl: publicUrl });

    return publicUrl;
}

// ─── Credits ──────────────────────────────────────────────────────────────────

/** Deduct credits from user (returns false if not enough) */
export async function deductCredits(
    uid: string,
    amount: number,
    currentCredits: number,
    isUnlimited: boolean
): Promise<boolean> {
    if (isUnlimited) return true;
    if (currentCredits < amount) return false;

    const newCredits = currentCredits - amount;
    await updateUserDocument(uid, { credits: newCredits });

    await addCreditHistory(uid, {
        action: 'Analyse durchgeführt',
        change: -amount,
        balance: newCredits,
    });

    return true;
}

/** Add credits to user */
export async function addCredits(
    uid: string,
    amount: number,
    currentCredits: number,
    reason: string = 'Credit-Aufladung'
): Promise<void> {
    const newCredits = currentCredits + amount;
    await updateUserDocument(uid, { credits: newCredits });
    await addCreditHistory(uid, {
        action: reason,
        change: amount,
        balance: newCredits,
    });
}

/** Monthly credit reset check and execution */
export async function checkAndResetCredits(
    uid: string,
    plan: string,
    lastCreditReset: string
): Promise<boolean> {
    const currentMonth = getCurrentMonth();
    if (lastCreditReset === currentMonth) return false;

    const planCredits: Record<string, number> = {
        Starter: 2500,
        Pro: 5000,
        Builder: 15000,
    };

    const credits = planCredits[plan] ?? 2500;
    await updateUserDocument(uid, { credits, lastCreditReset: currentMonth });

    await addCreditHistory(uid, {
        action: 'Monatliche Erneuerung',
        change: credits,
        balance: credits,
    });

    return true;
}

// ─── Credit Deduction ─────────────────────────────────────────────────────────

/** Deduct credits from a user and log to credit_history (fire-and-forget safe) */
export async function deductUserCredits(
    uid: string,
    amount: number,
    action: string = 'KI-Analyse'
): Promise<void> {
    // Read current credits first
    const { data: row, error: fetchErr } = await supabase
        .from('users')
        .select('credits')
        .eq('id', uid)
        .single();

    if (fetchErr) throw fetchErr;

    const current = (row?.credits as number) ?? 0;
    const newBalance = Math.max(0, current - amount);

    // Update balance
    const { error: updateErr } = await supabase
        .from('users')
        .update({ credits: newBalance })
        .eq('id', uid);

    if (updateErr) throw updateErr;

    // Log to credit_history
    await addCreditHistory(uid, { action, change: -amount, balance: newBalance });
}

// ─── Credit History ───────────────────────────────────────────────────────────

async function addCreditHistory(
    uid: string,
    data: { action: string; change: number; balance: number }
): Promise<void> {
    const { error } = await supabase.from('credit_history').insert({
        user_id: uid,
        action: data.action,
        change: data.change,
        balance: data.balance,
        date: new Date().toLocaleDateString('de-DE', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }),
    });
    if (error) throw error;
}

/** Get credit history for user */
export async function getCreditHistory(
    uid: string,
    limitCount: number = 50
): Promise<CreditHistoryRecord[]> {
    const { data, error } = await supabase
        .from('credit_history')
        .select('*')
        .eq('user_id', uid)
        .order('created_at', { ascending: false })
        .limit(limitCount);

    if (error) throw error;
    return (data ?? []).map((row) => ({
        id: row.id,
        userId: row.user_id,
        action: row.action,
        change: row.change,
        balance: row.balance,
        date: row.date,
        createdAt: row.created_at,
    }));
}

// ─── Ideas ────────────────────────────────────────────────────────────────────

/** Save a new idea analysis */
export async function saveIdeaRecord(
    uid: string,
    data: Omit<IdeaRecord, 'id' | 'userId' | 'createdAt'>
): Promise<string> {
    const { data: row, error } = await supabase.from('ideas').insert({
        user_id: uid,
        title: data.title,
        content: data.content,
        rating: data.rating,
        credits_used: data.creditsUsed,
        tags: data.tags ?? [],
        conversation_history: data.conversationHistory ?? [],
    }).select('id').single();

    if (error) throw error;
    return row.id;
}

/** Get all saved ideas for user */
export async function getIdeaHistory(
    uid: string,
    limitCount: number = 100
): Promise<IdeaRecord[]> {
    const { data, error } = await supabase
        .from('ideas')
        .select('*')
        .eq('user_id', uid)
        .order('created_at', { ascending: false })
        .limit(limitCount);

    if (error) throw error;
    return (data ?? []).map((row) => ({
        id: row.id,
        userId: row.user_id,
        title: row.title,
        content: row.content,
        rating: row.rating as 'good' | 'medium' | 'bad',
        creditsUsed: row.credits_used,
        createdAt: row.created_at,
        tags: row.tags ?? [],
        conversationHistory: (row.conversation_history as IdeaRecord['conversationHistory']) ?? [],
    }));
}

/** Get a single saved idea by id (scoped to the owner). */
export async function getIdeaById(
    uid: string,
    ideaId: string
): Promise<IdeaRecord | null> {
    const { data: row, error } = await supabase
        .from('ideas')
        .select('*')
        .eq('id', ideaId)
        .eq('user_id', uid)
        .maybeSingle();

    if (error) throw error;
    if (!row) return null;
    return {
        id: row.id,
        userId: row.user_id,
        title: row.title,
        content: row.content,
        rating: row.rating as 'good' | 'medium' | 'bad',
        creditsUsed: row.credits_used,
        createdAt: row.created_at,
        tags: row.tags ?? [],
        conversationHistory: (row.conversation_history as IdeaRecord['conversationHistory']) ?? [],
    };
}

/** Delete an idea record */
export async function deleteIdeaRecord(uid: string, ideaId: string): Promise<void> {
    const { error } = await supabase
        .from('ideas')
        .delete()
        .eq('id', ideaId)
        .eq('user_id', uid);
    if (error) throw error;
}

// ─── Admin: All Users ─────────────────────────────────────────────────────────

export interface AdminUser {
    uid: string;
    name: string;
    email: string;
    plan: string;
    credits: number;
    maxCredits: number;
    joinDate: string;
    updatedAt: string;
    totalSpent: number;
    isBlocked: boolean;
    isAdmin: boolean;
    subscriptionStatus: 'active' | 'cancelled' | 'overdue';
    cancelledAt: string | null;
    cancelledEffectiveDate: string | null;
    cancellationReason: string | null;
}

function rowToAdminUser(row: Record<string, unknown>): AdminUser {
    return {
        uid: row.id as string,
        name: (row.name as string) ?? '',
        email: (row.email as string) ?? '',
        plan: (row.plan as string) ?? 'Starter',
        credits: (row.credits as number) ?? 0,
        maxCredits: (row.max_credits as number) ?? 0,
        joinDate: (row.created_at as string) ?? new Date().toISOString(),
        updatedAt: (row.updated_at as string) ?? (row.created_at as string) ?? new Date().toISOString(),
        totalSpent: (row.total_spent as number) ?? 0,
        isBlocked: (row.is_blocked as boolean) ?? false,
        isAdmin: (row.is_admin as boolean) ?? false,
        subscriptionStatus: ((row.subscription_status as string) ?? 'active') as AdminUser['subscriptionStatus'],
        cancelledAt: (row.cancelled_at as string) ?? null,
        cancelledEffectiveDate: (row.cancelled_effective_date as string) ?? null,
        cancellationReason: (row.cancellation_reason as string) ?? null,
    };
}

/** Get all users for admin panel (uses SECURITY DEFINER RPC so is_admin is always returned) */
export async function getAllUsers(): Promise<AdminUser[]> {
    const { data, error } = await supabase.rpc('get_all_users_admin');

    if (error) {
        console.error('[DB] get_all_users_admin error:', error.message, error.code, error.details);
        throw error;
    }
    return ((data ?? []) as Record<string, unknown>[]).map(row => rowToAdminUser(row));
}

/** Get users filtered by plan */
export async function getUsersByPlan(plan: string): Promise<AdminUser[]> {
    const all = await getAllUsers();
    return all.filter(u => u.plan === plan);
}

/** Block or unblock a user */
export async function adminSetUserBlocked(uid: string, blocked: boolean): Promise<void> {
    const { error } = await supabase
        .from('users')
        .update({ is_blocked: blocked })
        .eq('id', uid);
    if (error) throw error;
}

/**
 * Cancel a user's subscription (admin).
 * Routed through SECURITY DEFINER RPC so RLS on payment_methods / users
 * doesn't block the cross-user UPDATE. The RPC:
 *   • sets subscription_status='cancelled'
 *   • stamps cancelled_at = NOW()
 *   • sets cancelled_effective_date = last second of current UTC month
 *   • invalidates every payment method (disabled_at = NOW())
 *   • is a no-op if the target user is an admin (enforce_admin_protection trigger also guards)
 * The `effectiveDate` param is kept for signature stability but the RPC always
 * anchors to end-of-month — industry standard, matches the user-side flow.
 */
export async function adminCancelSubscription(
    uid: string,
    reason: string,
    _effectiveDate?: string
): Promise<void> {
    const { error } = await supabase.rpc('admin_cancel_subscription' as any, {
        p_user_id: uid,
        p_reason: reason,
    });
    if (error) throw error;
}

/** Reactivate user subscription — also re-enables previously disabled payment methods */
export async function adminReactivateSubscription(uid: string): Promise<void> {
    const { error } = await supabase.rpc('admin_reactivate_subscription' as any, {
        p_user_id: uid,
    });
    if (error) throw error;
}

/** Pause user subscription (sets status to 'overdue', distinct from 'cancelled') */
export async function adminPauseSubscription(uid: string): Promise<void> {
    const { error } = await supabase.rpc('admin_pause_subscription' as any, {
        p_user_id: uid,
    });
    if (error) throw error;
}

/**
 * Update user plan (Admin action).
 *
 * Admin-forced plan changes start a fresh monthly allotment:
 *   Starter → 2,500
 *   Pro     → 5,000
 *   Builder → 15000
 *   Free    → 250
 *
 * The caller may pass an explicit override for `credits` (e.g. when granting a
 * free-account promo with bonus credits). If `credits` is provided and > 0 we
 * use that value; otherwise we set credits = max = plan allotment.
 *
 * `last_credit_reset` is stamped to the current month so the monthly refill
 * won't double-top-up on day 1 of next month.
 */
export async function adminUpdateUserPlan(uid: string, plan: string, credits?: number): Promise<void> {
    const planCredits: Record<string, number> = { Free: 250, Starter: 2500, Pro: 5000, Builder: 15000 };
    const canonicalMax = planCredits[plan] ?? 2500;

    // Start from the plan max unless the admin explicitly passed a larger amount (promo).
    const resolvedCredits = credits && credits > 0 ? credits : canonicalMax;
    const resolvedMax = Math.max(canonicalMax, resolvedCredits);

    const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

    const { error } = await supabase
        .from('users')
        .update({
            plan,
            credits: resolvedCredits,
            max_credits: resolvedMax,
            last_credit_reset: currentMonth,
            updated_at: new Date().toISOString(),
        })
        .eq('id', uid);

    if (error) throw error;
}

/** Add free credits to user */
export async function adminAddCredits(uid: string, credits: number): Promise<void> {
    // Fetch current credits for this specific user instead of loading all users
    const { data: row, error: fetchErr } = await supabase
        .from('users')
        .select('credits')
        .eq('id', uid)
        .single();
    if (fetchErr) throw fetchErr;
    const current = (row?.credits as number) ?? 0;
    const { error } = await supabase
        .from('users')
        .update({ credits: current + credits })
        .eq('id', uid);
    if (error) throw error;
}

/** Get admin statistics from real Supabase data */
export async function getAdminStats(): Promise<{
    totalUsers: number;
    activeSubscriptions: number;
    planDistribution: Record<string, number>;
    totalRevenue: number;
    blockedUsers: number;
    cancelledUsers: number;
}> {
    const { data, error } = await supabase.from('users').select('*');
    if (error) throw error;

    let activeSubscriptions = 0;
    let totalRevenue = 0;
    let blockedUsers = 0;
    let cancelledUsers = 0;
    const planDistribution: Record<string, number> = {};

    (data || []).forEach((user: any) => {
        if (user.subscription_status === 'active') activeSubscriptions++;
        if (user.subscription_status === 'cancelled') cancelledUsers++;
        if (user.is_blocked) blockedUsers++;
        
        totalRevenue += user.total_spent || 0;
        
        const plan = user.plan || 'Starter';
        planDistribution[plan] = (planDistribution[plan] || 0) + 1;
    });

    return {
        totalUsers: (data || []).length,
        activeSubscriptions,
        planDistribution,
        totalRevenue,
        blockedUsers,
        cancelledUsers,
    };
}

// ─── Admin: Coupons CRUD ───────────────────────────────────────────────────────

/** Get all coupons for admin */
export async function adminGetAllCoupons(): Promise<CouponDocument[]> {
    const { data, error } = await supabase.rpc('get_all_coupons_admin');
    if (error) {
        console.error('[DB] get_all_coupons_admin RPC error:', error.message, error.code, error.details);
        throw error;
    }
    return ((data ?? []) as Record<string, unknown>[]).map(row => ({
        id: row.id as string,
        code: row.code as string,
        plan: row.plan as string,
        duration: row.duration as number,
        durationUnit: row.duration_unit as 'days' | 'months',
        credits: row.credits as number,
        isActive: row.is_active as boolean,
        usedCount: (row.used_count as number) ?? 0,
        maxUses: (row.max_uses as number) ?? 0,
        category: row.category as 'registration' | 'trial',
        expiresAt: row.expires_at as string | null,
        createdAt: row.created_at as string,
    }));
}

/** Create a new coupon */
export async function adminCreateCoupon(coupon: Omit<CouponDocument, 'id' | 'createdAt' | 'usedCount'>): Promise<void> {
    const { error } = await supabase.rpc('create_coupon_admin', {
        p_code: coupon.code.toUpperCase(),
        p_plan: coupon.plan,
        p_duration: coupon.duration,
        p_duration_unit: coupon.durationUnit,
        p_credits: coupon.credits,
        p_is_active: coupon.isActive,
        p_max_uses: coupon.maxUses,
        p_category: coupon.category,
        p_expires_at: coupon.expiresAt ?? '',
    });
    if (error) throw error;
}

/** Update coupon active status */
export async function adminToggleCoupon(id: string, isActive: boolean): Promise<void> {
    const { error } = await supabase.rpc('toggle_coupon_admin', {
        p_coupon_id: id,
        p_active: isActive,
    });
    if (error) throw error;
}

/** Delete a coupon */
export async function adminDeleteCoupon(id: string): Promise<void> {
    const { error } = await supabase.rpc('delete_coupon_admin', { p_coupon_id: id });
    if (error) throw error;
}

// ─── Admin: Tips of the Day ────────────────────────────────────────────────────

/** Get all tips */
export async function adminGetTips(): Promise<unknown[]> {
    const data = await getWebsiteSettings('admin_tips_of_day');
    return Array.isArray(data) ? data : [];
}

/** Save all tips */
export async function adminSaveTips(tips: unknown[]): Promise<void> {
    await saveWebsiteSettings('admin_tips_of_day', tips);
    localStorage.setItem('admin_tips_of_day', JSON.stringify(tips));
}

// ─── Admin: Credit History ─────────────────────────────────────────────────────

/** Get recent credit history across all users (for Transaction Log) */
export async function adminGetCreditHistory(limit = 100): Promise<{
    id: string;
    userId: string;
    userName: string;
    userEmail: string;
    action: string;
    change: number;
    balance: number;
    date: string;
}[]> {
    const { data, error } = await supabase.rpc('get_all_credit_history_admin', { p_limit: limit });
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(row => ({
        id: row.id as string,
        userId: row.user_id as string,
        userName: (row.user_name as string) || '',
        userEmail: (row.user_email as string) || '',
        action: row.action as string,
        change: row.change as number,
        balance: row.balance as number,
        date: row.date as string,
    }));
}

// ─── Coupons ──────────────────────────────────────────────────────────────────

/** Get coupon by code */
export async function getCoupon(code: string): Promise<CouponDocument | null> {
    const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', code.toUpperCase())
        .eq('is_active', true)
        .single();

    if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
    }
    if (!data) return null;

    return {
        id: data.id,
        code: data.code,
        plan: data.plan,
        duration: data.duration,
        durationUnit: data.duration_unit as 'days' | 'months',
        credits: data.credits,
        isActive: data.is_active,
        usedCount: data.used_count,
        maxUses: data.max_uses,
        category: data.category as 'registration' | 'trial',
        expiresAt: data.expires_at,
        createdAt: data.created_at,
    };
}

/** Mark coupon as used */
export async function useCoupon(couponId: string, usedCount: number): Promise<void> {
    const newUsedCount = usedCount + 1;

    // Get current max_uses to check if should deactivate
    const { data: coupon } = await supabase
        .from('coupons')
        .select('max_uses')
        .eq('id', couponId)
        .single();

    const updateData: Record<string, unknown> = { used_count: newUsedCount };
    if (coupon && newUsedCount >= coupon.max_uses) {
        updateData.is_active = false;
    }

    const { error } = await supabase
        .from('coupons')
        .update(updateData)
        .eq('id', couponId);

    if (error) throw error;
}

// ─── Website Settings (CMS) ───────────────────────────────────────────────────

/** Fetch a single website setting by key */
export async function getWebsiteSettings(key: string): Promise<unknown | null> {
    const { data, error } = await supabase
        .from('website_settings')
        .select('value')
        .eq('key', key)
        .maybeSingle();
    if (error) return null;
    return data?.value ?? null;
}

/** Fetch all website settings as a key→value map */
export async function getAllWebsiteSettings(): Promise<Record<string, unknown>> {
    const { data, error } = await supabase
        .from('website_settings')
        .select('key, value');
    if (error || !data) return {};
    return Object.fromEntries(data.map((row) => [row.key, row.value]));
}

/** Upsert a single website setting */
export async function saveWebsiteSettings(key: string, value: unknown): Promise<void> {
    const { error } = await supabase
        .from('website_settings')
        .upsert({ key, value: value as Json, updated_at: new Date().toISOString() });
    if (error) throw error;
}

// ─── Downgrade Management ─────────────────────────────────────────────────────

/** Fetch all downgrade requests */
export async function adminGetAllDowngradeRequests(): Promise<DowngradeRequest[]> {
    const { data, error } = await supabase
        .from('downgrade_requests')
        .select('*')
        .order('date', { ascending: false });
    if (error) throw error;
    return (data || []).map((row: any) => ({
        id: row.id,
        userId: row.user_id,
        userName: row.user_name,
        userEmail: row.user_email,
        currentPlan: row.current_plan,
        requestedPlan: row.requested_plan,
        reason: row.reason,
        date: row.date,
        status: row.status,
    }));
}

/** Update status of a downgrade request */
export async function adminUpdateDowngradeRequestStatus(
    id: string,
    status: 'approved' | 'rejected'
): Promise<void> {
    const { error } = await supabase
        .from('downgrade_requests')
        .update({ status })
        .eq('id', id);
    if (error) throw error;
}

/** CRUD for Downgrade Rules */
export async function adminGetAllDowngradeRules(): Promise<DowngradeRule[]> {
    const { data, error } = await supabase
        .from('downgrade_rules')
        .select('*')
        .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((row: any) => ({
        id: row.id,
        name: row.name,
        description: row.description,
        enabled: row.enabled,
        conditions: row.conditions,
        action: row.action,
        createdAt: row.created_at,
        triggeredCount: row.triggered_count,
    }));
}

export async function adminSaveDowngradeRule(rule: Partial<DowngradeRule>): Promise<void> {
    const payload: any = {
        name: rule.name,
        description: rule.description,
        enabled: rule.enabled,
        conditions: rule.conditions,
        action: rule.action,
        updated_at: new Date().toISOString(),
    };
    if (rule.id) {
        const { error } = await supabase
            .from('downgrade_rules')
            .update(payload)
            .eq('id', rule.id);
        if (error) throw error;
    } else {
        const { error } = await supabase
            .from('downgrade_rules')
            .insert(payload);
        if (error) throw error;
    }
}

export async function adminDeleteDowngradeRule(id: string): Promise<void> {
    const { error } = await supabase
        .from('downgrade_rules')
        .delete()
        .eq('id', id);
    if (error) throw error;
}

/** Manage Refunds History */
export async function adminGetAllDowngradeRefunds(): Promise<RefundRecord[]> {
    const { data, error } = await supabase
        .from('downgrade_refunds')
        .select('*')
        .order('date', { ascending: false });
    if (error) throw error;
    return (data || []).map((row: any) => ({
        id: row.id,
        requestId: row.request_id,
        userId: row.user_id,
        userName: row.user_name,
        fromPlan: row.from_plan,
        toPlan: row.to_plan,
        amount: row.amount,
        calculationType: row.calculation_type,
        daysRemaining: row.days_remaining,
        totalDays: row.total_days,
        date: row.date,
        status: row.status,
    }));
}

export async function adminAddDowngradeRefund(refund: Omit<RefundRecord, 'id'>): Promise<void> {
    const { error } = await supabase
        .from('downgrade_refunds')
        .insert({
            request_id: refund.requestId,
            user_id: refund.userId,
            user_name: refund.userName,
            from_plan: refund.fromPlan,
            to_plan: refund.toPlan,
            amount: refund.amount,
            calculation_type: refund.calculationType,
            days_remaining: refund.daysRemaining,
            total_days: refund.totalDays,
            date: refund.date,
            status: refund.status,
        });
    if (error) throw error;
}

export async function getBillingAddress(uid: string): Promise<any | null> {
    const { data } = (await supabase.from('billing_addresses' as any).select('*').eq('user_id', uid).single()) as { data: any };
    if (!data) return null;
    return {
        id: data.id, userId: data.user_id, name: data.name, company: data.company,
        street: data.street, zip: data.zip, city: data.city, country: data.country, vatId: data.vat_id
    };
}

export async function upsertBillingAddress(uid: string, address: any): Promise<void> {
    const { error } = await supabase.from('billing_addresses' as any).upsert({
        user_id: uid, name: address.name, company: address.company,
        street: address.street, zip: address.zip, city: address.city, country: address.country, vat_id: address.vatId
    });
    if (error) throw error;
}

export async function getPaymentMethods(uid: string): Promise<any[]> {
    const { data, error } = (await supabase.from('payment_methods' as any).select('*').eq('user_id', uid)) as { data: any[], error: any };
    if (error) throw error;
    return (data || []).map(row => ({
        id: row.id, userId: row.user_id, type: row.type, last4: row.last4,
        expiry: row.expiry, holderName: row.holder_name, isDefault: row.is_default,
        // disabled_at is set by the DB when the subscription is cancelled
        disabledAt: row.disabled_at as string | null,
    }));
}

// ─── User-side subscription RPCs (upgrade / downgrade / cancel / resume) ──────

/** User upgrades their own plan. Adds the credit difference and reactivates. */
export async function userUpgradePlan(newPlan: 'Starter' | 'Pro' | 'Builder'): Promise<void> {
    const { error } = await supabase.rpc('user_upgrade_plan' as any, { p_new_plan: newPlan });
    if (error) throw error;
}

/**
 * User requests a downgrade. If admin_auto_approve_downgrades=true in
 * website_settings the plan change is applied immediately; otherwise a
 * pending downgrade_requests row is created for admin review.
 * Returns whether the change was auto-approved.
 */
export async function userRequestDowngrade(
    requestedPlan: 'Starter' | 'Pro' | 'Builder' | 'Free',
    reason: string | null = null,
): Promise<{ requestId: string; autoApproved: boolean; newPlan?: string }> {
    const { data, error } = await supabase.rpc('user_request_downgrade' as any, {
        p_requested_plan: requestedPlan,
        p_reason: reason,
    });
    if (error) throw error;
    const row = (data || {}) as { request_id: string; auto_approved: boolean; new_plan?: string };
    return { requestId: row.request_id, autoApproved: !!row.auto_approved, newPlan: row.new_plan };
}

/**
 * User cancels their own subscription — effective end of current month.
 * All payment methods are disabled so no further charges can occur.
 */
export async function userCancelSubscription(reason: string | null = null): Promise<string> {
    const { data, error } = await supabase.rpc('user_cancel_subscription' as any, { p_reason: reason });
    if (error) throw error;
    return data as unknown as string; // ISO timestamp of effective cancellation
}

/** User re-activates a pending cancellation (only works before effective date). */
export async function userResumeSubscription(): Promise<void> {
    const { error } = await supabase.rpc('user_resume_subscription' as any);
    if (error) throw error;
}

/**
 * Calendar-aligned maintenance: finalize expired cancellations + monthly
 * credit refill. Safe to call on every app bootstrap; RPC is idempotent.
 */
export async function runSubscriptionMaintenance(): Promise<void> {
    const { error } = await supabase.rpc('run_subscription_maintenance' as any);
    if (error && error.code !== 'PGRST202') {
        // PGRST202 = function not found (older deployment) — swallow gracefully
        console.warn('[DB] run_subscription_maintenance failed:', error.message);
    }
}

export async function addPaymentMethod(uid: string, card: any): Promise<void> {
    if (card.isDefault) {
        await supabase.from('payment_methods' as any).update({ is_default: false }).eq('user_id', uid);
    }
    const { error } = await supabase.from('payment_methods' as any).insert({
        user_id: uid, type: card.type, last4: card.last4, expiry: card.expiry,
        holder_name: card.holderName, is_default: card.isDefault
    });
    if (error) throw error;
}

export async function deletePaymentMethod(uid: string, cardId: string): Promise<void> {
    const { error } = await supabase.from('payment_methods' as any).delete().eq('id', cardId).eq('user_id', uid);
    if (error) throw error;
}

export async function setDefaultPaymentMethod(uid: string, cardId: string): Promise<void> {
    await supabase.from('payment_methods' as any).update({ is_default: false }).eq('user_id', uid);
    const { error } = await supabase.from('payment_methods' as any).update({ is_default: true }).eq('id', cardId).eq('user_id', uid);
    if (error) throw error;
}

export async function getInvoices(uid: string): Promise<any[]> {
    const { data, error } = (await supabase.from('invoices' as any).select('*').eq('user_id', uid).order('date', { ascending: false })) as { data: any[], error: any };
    if (error) throw error;
    return (data || []).map(row => ({
        id: row.id, userId: row.user_id, invoice: row.invoice_number, amount: row.amount,
        tax: row.tax, currency: row.currency, description: row.description, status: row.status, date: new Date(row.date).toISOString().split('T')[0]
    }));
}

export async function getPlans(): Promise<any[]> {
    const { data, error } = await supabase.from('plans').select('*').order('price', { ascending: true });
    if (error) throw error;
    return data || [];
}

// ─── Admin: AI Usage Logs ──────────────────────────────────────────────────────

export interface AdminAiUsageLog {
    id: string;
    createdAt: string;
    userId: string;
    userEmail: string;
    userName: string;
    model: string;
    creditsCharged: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    providerCostUsd: number;
    revenueUsd: number;
    marginUsd: number;
    processingMs: number;
    rating: string | null;
    fromCache: boolean;
    providers: Record<string, unknown> | null;
}

export interface AdminAiUsageLogsPage {
    rows: AdminAiUsageLog[];
    totalCount: number;
}

export interface AdminAiUsageSummary {
    totalRequests: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalTokens: number;
    totalCostUsd: number;
    totalRevenueUsd: number;
    totalMarginUsd: number;
    totalCredits: number;
    uniqueUsers: number;
    avgCostPerRequest: number;
    byModel: Record<string, {
        requests: number;
        inputTokens: number;
        outputTokens: number;
        costUsd: number;
        revenueUsd: number;
        marginUsd: number;
        avgCostUsd: number;
    }>;
}

export interface AdminAiUsageLogDetail {
    id: string;
    createdAt: string;
    userId: string;
    userEmail: string;
    userName: string;
    model: string;
    requestHash: string;
    inputTokens: number;
    outputTokens: number;
    providerCostUsd: number;
    providers: Record<string, unknown> | null;
    response: Record<string, unknown> | null;
}

/** Paginated admin fetch of AI request logs */
export async function adminGetAiUsageLogs(opts: {
    limit?: number;
    offset?: number;
    search?: string | null;
    model?: string | null;
    dateFrom?: string | null;
    dateTo?: string | null;
} = {}): Promise<AdminAiUsageLogsPage> {
    const { data, error } = await supabase.rpc('admin_get_ai_usage_logs' as any, {
        p_limit: opts.limit ?? 25,
        p_offset: opts.offset ?? 0,
        p_search: opts.search ?? null,
        p_model: opts.model ?? null,
        p_date_from: opts.dateFrom ?? null,
        p_date_to: opts.dateTo ?? null,
    });
    if (error) throw error;
    const rows = (data ?? []) as Record<string, unknown>[];
    const totalCount = rows.length > 0 ? Number(rows[0].total_count ?? 0) : 0;
    return {
        totalCount,
        rows: rows.map((r) => ({
            id: r.id as string,
            createdAt: r.created_at as string,
            userId: r.user_id as string,
            userEmail: (r.user_email as string) || '',
            userName: (r.user_name as string) || '',
            model: (r.model as string) || 'unknown',
            creditsCharged: Number(r.credits_charged ?? 0),
            inputTokens: Number(r.input_tokens ?? 0),
            outputTokens: Number(r.output_tokens ?? 0),
            totalTokens: Number(r.total_tokens ?? 0),
            providerCostUsd: Number(r.provider_cost_usd ?? 0),
            revenueUsd: Number(r.revenue_usd ?? 0),
            marginUsd: Number(r.margin_usd ?? 0),
            processingMs: Number(r.processing_ms ?? 0),
            rating: (r.rating as string) ?? null,
            fromCache: Boolean(r.from_cache ?? false),
            providers: (r.providers as Record<string, unknown>) ?? null,
        })),
    };
}

/** Aggregated AI usage summary stats */
export async function adminGetAiUsageSummary(period: 'today' | '7d' | '30d' | 'all' = 'all'): Promise<AdminAiUsageSummary> {
    const { data, error } = await supabase.rpc('admin_get_ai_usage_summary' as any, { p_period: period });
    if (error) throw error;
    const row = (Array.isArray(data) && data.length > 0 ? data[0] : {}) as Record<string, unknown>;
    return {
        totalRequests: Number(row.total_requests ?? 0),
        totalInputTokens: Number(row.total_input_tokens ?? 0),
        totalOutputTokens: Number(row.total_output_tokens ?? 0),
        totalTokens: Number(row.total_tokens ?? 0),
        totalCostUsd: Number(row.total_cost_usd ?? 0),
        totalRevenueUsd: Number(row.total_revenue_usd ?? 0),
        totalMarginUsd: Number(row.total_margin_usd ?? 0),
        totalCredits: Number(row.total_credits ?? 0),
        uniqueUsers: Number(row.unique_users ?? 0),
        avgCostPerRequest: Number(row.avg_cost_per_request ?? 0),
        byModel: (row.by_model as AdminAiUsageSummary['byModel']) ?? {},
    };
}

/** Full detail for a single AI request log entry (request/response payload) */
export async function adminGetAiUsageLogDetail(id: string): Promise<AdminAiUsageLogDetail | null> {
    const { data, error } = await supabase.rpc('admin_get_ai_usage_log_detail' as any, { p_id: id });
    if (error) throw error;
    const row = (Array.isArray(data) && data.length > 0 ? data[0] : null) as Record<string, unknown> | null;
    if (!row) return null;
    return {
        id: row.id as string,
        createdAt: row.created_at as string,
        userId: row.user_id as string,
        userEmail: (row.user_email as string) || '',
        userName: (row.user_name as string) || '',
        model: (row.model as string) || 'unknown',
        requestHash: (row.request_hash as string) || '',
        inputTokens: Number(row.input_tokens ?? 0),
        outputTokens: Number(row.output_tokens ?? 0),
        providerCostUsd: Number(row.provider_cost_usd ?? 0),
        providers: (row.providers as Record<string, unknown>) ?? null,
        response: (row.response as Record<string, unknown>) ?? null,
    };
}

export type { UserDocument, IdeaRecord, CreditHistoryRecord, CouponDocument, DowngradeRequest, DowngradeRule, RefundRecord };
